import React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Button from "@mui/material/Button";
import { Panel } from "@poseidon/ui/components/Panel";
import TableHead from "@mui/material/TableHead";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useRateMatrixState } from "./useRateMatrixState";
import LinearProgress from "@mui/material/LinearProgress";
import { InputCell } from "./InputCell";
import { usePermissions } from "@poseidon/ui/components";
import Alert from "@mui/material/Alert";

const RateRow = ({
  row,
  onDelete,
  onChange,
  onBlur,
  isLast,
  validationErrors,
}) => {
  const rowErrors = validationErrors?.find((v) => v.id === row.id);
  return (
    <>
      <TableRow
        sx={{
          borderBottom: 1,
          borderBottomColor: "neutral.500",
          borderBottomStyle: "solid",
        }}
      >
        <TableCell></TableCell>
        <InputCell
          row={row}
          field="fromMonths"
          type="term"
          onChange={onChange}
          onBlur={onBlur}
          min={0}
          max={parseInt(row.toMonths) - 1}
          error={rowErrors?.fieldErrors?.fromMonths}
        />
        <InputCell
          row={row}
          field="toMonths"
          type="term"
          onChange={onChange}
          onBlur={onBlur}
          allowInfinity={isLast}
          min={parseInt(row.fromMonths) + 1}
          error={rowErrors?.fieldErrors?.toMonths}
        />
        <InputCell
          row={row}
          field="rate"
          type="rate"
          onChange={onChange}
          onBlur={onBlur}
          min={0}
          max={100}
          error={rowErrors?.fieldErrors?.rate}
        />
        <TableCell>
          <IconButton onClick={onDelete} aria-label="delete">
            <DeleteIcon />
          </IconButton>
        </TableCell>
      </TableRow>
      {rowErrors?.message && (
        <TableRow>
          <TableCell></TableCell>
          <TableCell colSpan={4} sx={{ pt: 0 }}>
            <Alert variant="outlined" severity="error">
              {rowErrors?.message}
            </Alert>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const AmountRange = ({
  row,
  onDelete,
  onChange,
  addChild,
  deleteChild,
  updateChild,
  onBlur,
  isLast,
  validationErrors,
}) => {
  const rowErrors = validationErrors?.find((v) => v.id === row.id);
  return (
    <>
      <TableRow className="rateMatrixTable__amountRow">
        <TableCell>
          <IconButton onClick={onDelete} aria-label="delete">
            <DeleteIcon />
          </IconButton>
        </TableCell>
        <InputCell
          row={row}
          field="fromAmount"
          type="amount"
          disabled={true}
          error={rowErrors?.fieldErrors?.fromAmount}
        />
        <InputCell
          row={row}
          field="toAmount"
          type="amount"
          onChange={onChange}
          onBlur={onBlur}
          allowInfinity={isLast}
          error={rowErrors?.fieldErrors?.toAmount}
        />
        <TableCell></TableCell>
        <TableCell>
          <IconButton onClick={addChild} aria-label="add">
            <AddIcon />
          </IconButton>
        </TableCell>
      </TableRow>
      {row.rows.map((row, index, all) => (
        <RateRow
          key={row.id}
          isLast={index === all.length - 1}
          row={row}
          onDelete={() => deleteChild(row.id)}
          onChange={(updates) => updateChild(row.id, updates)}
          onBlur={onBlur}
          validationErrors={validationErrors}
        />
      ))}
    </>
  );
};

export default function RateMatrix() {
  const { canWrite } = usePermissions();
  const {
    rows,
    actions,
    loading,
    loadError,
    saving,
    handleSave,
    validationErrors,
  } = useRateMatrixState();
  if (loading) {
    return <LinearProgress />;
  }
  if (loadError) {
    return <Alert severity="error">Error loading rate matrix</Alert>;
  }
  return (
    <Panel
      title="Rate Matrix"
      nopadding={false}
      expandable={true}
      actions={
        <Button
          size="small"
          onClick={handleSave}
          disabled={saving || !canWrite}
        >
          Save
        </Button>
      }
    >
      <Table
        sx={{
          "& .rateMatrixTable__amountRow:not(:first-of-type)": {
            borderTopWidth: 2,
            borderTopColor: "neutral.200",
            borderTopStyle: "solid",
          },
          "& *": {
            borderCollapse: false,
          },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell>
              <IconButton onClick={() => actions.add()} aria-label="add">
                <AddIcon />
              </IconButton>
            </TableCell>
            <TableCell>From</TableCell>
            <TableCell>To</TableCell>
            <TableCell>Rate</TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index, all) => (
            <AmountRange
              key={row.id}
              row={row}
              isLast={index === all.length - 1}
              onDelete={() => actions.delete(row.id)}
              addChild={() => actions.addChild(row.id)}
              updateChild={(id, updates) =>
                actions.updateChild(row.id, id, updates)
              }
              deleteChild={(id) => actions.deleteChild(row.id, id)}
              onBlur={() => actions.sort()}
              onChange={(updates) => actions.update(row.id, updates)}
              validationErrors={validationErrors}
            />
          ))}
        </TableBody>
      </Table>
      {saving && <LinearProgress />}
    </Panel>
  );
}
